/**
 * Time-range selection shared by every observability surface (design 19 §7,
 * VSNP-1218).
 *
 * The TYPES and the RESOLVER live here, apart from the control in
 * `components/observability/TimeRange.tsx`, for one reason that matters:
 *
 *   a RELATIVE range must not be resolved when the user picks it.
 *
 * "Last 1 hour" chosen at 09:00 and still displayed at 11:00 means the last hour
 * *now*, so every fetch calls `resolveTimeRange` again. A component that stored
 * the resolved instants would quietly freeze its charts at the moment of
 * selection and look exactly like a broken auto-refresh.
 */

export type TimeRangePreset = '15m' | '1h' | '6h' | '24h' | '7d' | '30d' | 'custom';

export interface TimeRangeValue {
  preset: TimeRangePreset;
  /** `datetime-local` strings; only meaningful when preset === 'custom'. */
  from?: string;
  to?: string;
}

export interface ResolvedRange {
  /** Unix seconds. */
  start: number;
  /** Unix seconds. */
  end: number;
  /** PromQL/UI step in seconds. */
  step: number;
  /** Range-vector window for rate()/increase() — always ≥ 4 steps. */
  window: string;
  /** Human label ('1h', 'custom'). */
  label: string;
  durationSeconds: number;
  /** ISO instants, for the JSON APIs (sessions, audit events, export). */
  fromIso: string;
  toIso: string;
}

export const DEFAULT_TIME_RANGE: TimeRangeValue = { preset: '1h' };

export const TIME_RANGE_PRESETS: { key: Exclude<TimeRangePreset, 'custom'>; label: string; seconds: number; step: number; window: string }[] = [
  { key: '15m', label: 'Last 15 min', seconds: 900, step: 30, window: '5m' },
  { key: '1h', label: 'Last 1 hour', seconds: 3600, step: 60, window: '5m' },
  { key: '6h', label: 'Last 6 hours', seconds: 21600, step: 300, window: '20m' },
  { key: '24h', label: 'Last 24 hours', seconds: 86400, step: 600, window: '40m' },
  { key: '7d', label: 'Last 7 days', seconds: 604800, step: 3600, window: '4h' },
  { key: '30d', label: 'Last 30 days', seconds: 2592000, step: 14400, window: '16h' },
];

/** A step that keeps a custom window near ~200 points without going sub-scrape. */
function stepFor(durationSeconds: number): { step: number; window: string } {
  const preset = TIME_RANGE_PRESETS.find((p) => durationSeconds <= p.seconds);
  if (preset) return { step: preset.step, window: preset.window };
  // Longer than 30 d: one point per ~1/200th of the window, rounded to hours.
  const step = Math.max(14400, Math.ceil(durationSeconds / 200 / 3600) * 3600);
  return { step, window: `${Math.round((step * 4) / 3600)}h` };
}

/**
 * Resolve a selection into concrete query bounds. Call this at FETCH time, not
 * at selection time (see the file header).
 */
export function resolveTimeRange(value: TimeRangeValue, now: number = Date.now()): ResolvedRange {
  if (value.preset === 'custom' && value.from && value.to) {
    const start = Math.floor(new Date(value.from).getTime() / 1000);
    const end = Math.floor(new Date(value.to).getTime() / 1000);
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
      const duration = end - start;
      const { step, window } = stepFor(duration);
      return {
        start,
        end,
        step,
        window,
        label: 'custom',
        durationSeconds: duration,
        fromIso: new Date(start * 1000).toISOString(),
        toIso: new Date(end * 1000).toISOString(),
      };
    }
  }

  const preset = TIME_RANGE_PRESETS.find((p) => p.key === value.preset) ?? TIME_RANGE_PRESETS[1];
  const end = Math.floor(now / 1000);
  const start = end - preset.seconds;
  return {
    start,
    end,
    step: preset.step,
    window: preset.window,
    label: preset.key,
    durationSeconds: preset.seconds,
    fromIso: new Date(start * 1000).toISOString(),
    toIso: new Date(end * 1000).toISOString(),
  };
}

/**
 * Average a GAUGE expression over the whole selected range, as a PromQL
 * subquery (design §9b).
 *
 * A stat card showing the instantaneous value of a gauge answers a question
 * nobody asked of a 30-day view: `agw_sessions_live` sampled at the moment the
 * page loads reads 0 at 3 a.m. for a platform that carried four hundred
 * sessions that month, and the card is then indistinguishable from a broken
 * query. Averaging over the picker's range makes the number mean what the
 * range says it means.
 *
 * The expression is parenthesised before the subquery bracket: `sum(x)[1h:1m]`
 * is a parse error in PromQL, and `sum(x[1h:1m])` would be a different query
 * entirely (a subquery of the inner selector rather than of the aggregate).
 */
export function avgOverRange(expr: string, r: Pick<ResolvedRange, 'durationSeconds' | 'step'>): string {
  return `avg_over_time((${expr})[${r.durationSeconds}s:${r.step}s])`;
}

/** The same shape, for the peak of a gauge rather than its mean. */
export function maxOverRange(expr: string, r: Pick<ResolvedRange, 'durationSeconds' | 'step'>): string {
  return `max_over_time((${expr})[${r.durationSeconds}s:${r.step}s])`;
}

/**
 * The range as a PromQL duration, for `increase()` over the whole window.
 *
 * Seconds rather than a pretty unit: the picker's own `window` is the RATE
 * window (a fraction of the range, chosen to keep a chart smooth), and a
 * counter card wants the TOTAL over the range. The two are different numbers
 * and conflating them is exactly the bug doc 22 §10.4 describes.
 */
export function rangeDuration(r: Pick<ResolvedRange, 'durationSeconds'>): string {
  return `${r.durationSeconds}s`;
}

/** A human label for the selected range, for panel titles that state a window. */
export function rangeLabel(value: TimeRangeValue): string {
  const preset = TIME_RANGE_PRESETS.find((p) => p.key === value.preset);
  if (preset) return preset.label.replace(/^Last /, '');
  return 'the selected range';
}
